import { formatDate } from "@/lib/utils";
import { Badge, Text } from "@a7y/tailwind-catalyst";
import type { ReactNode } from "react";
import { Section, StatCard } from "../shared";
import type { AffiliateDetailResponse } from "../types";

export function OverviewTab({ detail }: { detail: AffiliateDetailResponse }) {
  const { affiliate, links, referrals, commissions, payouts } = detail;

  // A7Y-2190: counts are cumulative down the funnel — a lead that converts must
  // still count as a lead, so `leads` includes conversions rather than bucketing
  // mutually-exclusively on current state (which made the count shrink on
  // promotion).
  const conversions = referrals.filter((r) => r.state === "conversion").length;
  const leads = referrals.filter((r) => r.state === "lead" || r.state === "conversion").length;

  const rows: Array<{ label: string; value: ReactNode }> = [
    { label: "Email", value: <span className="font-mono">{affiliate.email}</span> },
    { label: "Name", value: affiliate.name || "—" },
    { label: "Promotion channel", value: affiliate.promotionChannel || "—" },
    { label: "Campaign", value: <span className="font-mono">{affiliate.campaignId}</span> },
    {
      label: "Customer discount",
      value:
        affiliate.customerDiscountPercent != null
          ? `${affiliate.customerDiscountPercent}%`
          : "—",
    },
    {
      label: "User linked",
      value: affiliate.userId ? (
        <span className="font-mono">{affiliate.userId}</span>
      ) : (
        <Badge color="zinc">Invite-only (unclaimed)</Badge>
      ),
    },
    {
      label: "Email confirmed",
      value: affiliate.emailConfirmedAt ? (
        formatDate(affiliate.emailConfirmedAt)
      ) : (
        <Badge color="amber">Unconfirmed</Badge>
      ),
    },
    {
      label: "ToS accepted",
      value: affiliate.tosAcceptedAt
        ? `${formatDate(affiliate.tosAcceptedAt)}${affiliate.tosVersion ? ` (v${affiliate.tosVersion})` : ""}`
        : "—",
    },
    { label: "Created", value: formatDate(affiliate.createdAt) },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Links" value={links.length} />
        <StatCard label="Leads" value={leads} />
        <StatCard label="Conversions" value={conversions} />
        <StatCard label="Commissions" value={commissions.length} sub={`${payouts.length} payouts`} />
      </div>

      <Section title="Account">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.label} className="flex flex-col gap-0.5 text-sm">
              <dt className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                {row.label}
              </dt>
              <dd className="text-zinc-900 dark:text-zinc-100">{row.value}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {affiliate.migratedStats && (
        <Section title="Migrated (Rewardful) stats">
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Clicks" value={affiliate.migratedStats.clicks} />
            <StatCard label="Leads" value={affiliate.migratedStats.leads} />
            <StatCard label="Conversions" value={affiliate.migratedStats.conversions} />
          </div>
          <Text className="mt-3 text-xs text-zinc-400">
            Imported {formatDate(affiliate.migratedStats.importedAt)} from{" "}
            {affiliate.migratedStats.source}.
          </Text>
        </Section>
      )}
    </div>
  );
}
